import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps, GridItemStyle, GridWidths } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Linkedin from "@insite/mobius/Icons/Linkedin";
import Mail from "@insite/mobius/Icons/Mail";
import Link, { StyledIcon } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select from "@insite/mobius/Select";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadCompanyDirectory from "@tSchaedlerYesco/Handlers/CompanyDirectory/LoadCompanyDirectory";
import cloneDeep from "lodash/cloneDeep";
import React, { FC, useEffect, useState } from "react";
import { render } from "react-dom";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        location: getLocation(state),
    };
};

export interface CustomCompanyDirectoryStyles {
    container?: InjectableCss;
    dataTable?: DataTableProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    companyDirectoryGridItem?: GridItemProps;
    filterHeading: InjectableCss;
    companyDirectoryFilter: GridItemProps;
    filterWrapper: InjectableCss;
    iconStyle?: InjectableCss;
    imgStyle?: InjectableCss;
}

export const CustomCompanyDirectoryStyles: CustomCompanyDirectoryStyles = {
    container: {
        css: css`
            padding-top: 0px;
            height: 500px;
            overflow: auto;
        `,
    },
    iconStyle: {
        css: css`
            color: #3666b1;
            margin-left: 15px;
            svg {
                color: #3666b1;
                width: 100%;
                height: 100%;
            }
        `,
    },
    imgStyle: {
        css: css`
            margin-left: 10px;
        `,
    },
    dataTable: {
        css: css`
            border: 1px solid #cccccc;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    companyDirectoryGridItem: {
        width: [12, 12, 12, 9, 9],
    },
    filterHeading: {
        css: css`
            h2 {
                margin-bottom: 15px;
                font-size: 25px;
                color: #474747;
                font-weight: bold;
                margin-right: 15px;
            }
        `,
    },
    companyDirectoryFilter: {
        css: css`
            position: relative;
            top: 52px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            top: 55px;
                            width: 65%;
                        `,
                        css`
                            top: 55px;
                            width: 65%;
                        `,
                        null,
                        null,
                    ],
                    "minMax",
                );
            }}
            button[class^="ButtonWrapper"] {
                > span[class^="TypographyStyle"] {
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    font-size: 13px;
                                `,
                                css`
                                    font-size: 13px;
                                `,
                                null,
                                null,
                            ],
                            "minMax",
                        );
                    }}
                }
            }
        `,
    },
    filterWrapper: {
        css: css`
            margin-bottom: 20px;
            margin-right: 20px;
            h2[class^="TypographyStyle"] {
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: 15px;
                            `,
                            css`
                                font-size: 15px;
                            `,
                            null,
                            null,
                        ],
                        "minMax",
                    );
                }}
            }
            > div {
                > div[class^="FormFieldStyle"] {
                    > div[class^="FormInputWrapper"] {
                        > select {
                            padding-right: 40px;
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                        }
                    }
                }
            }
        `,
    },
};

const mapDispatchToProps = {
    loadCompanyDirectory,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const getQueryStringValue = (key: string): string => {
    return decodeURIComponent(
        window.location.search.replace(
            new RegExp(`^(?:.*[&\\?]${encodeURIComponent(key).replace(/[\.\+\*]/g, "\\$&")}(?:\\=([^&]*))?)?.*$`, "i"),
            "$1",
        ),
    );
};

const updateQueryStringParameter = (uri: string, key: string, value: string): string => {
    const re = new RegExp(`([?&])${key}=.*?(&|$)`, "i");
    const separator = uri.indexOf("?") !== -1 ? "&" : "?";
    if (uri.match(re)) {
        return uri.replace(re, `$1${key}=${value}$2`);
    }

    return `${uri + separator + key}=${value}`;
};

const CompanyDirectoryGrid: FC<Props> = ({ location, loadCompanyDirectory }) => {
    const [companyData, setCompanyData] = useState<any>([]);
    const [backupCompanyData, setBackupCompanyData] = useState<any>([]);
    const [copyData, setCopyData] = useState<any>([]);
    const [selectedRole, setSelectedRole] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedBranchName, setSelectedBranchName] = useState("");
    const [isPageReloadRequired, setIsPageReloadRequired] = useState(true);
    const [branchNamesData, setBranchNameData] = useState<string[]>([]);

    const roles: string[] = [
        "OUTSIDE SALES",
        "INSIDE SALES",
        // "COUNTER SALES",
        "MANAGEMENT",
        "SPECIALIST",
        "CREDIT",
        "OPERATIONS",
        "WAREHOUSE",
    ];

    const categories: string[] = [
        "AUTOMATION & CONTROLS",
        "NETWORKING & DATACOMM",
        "LIGHTING & CONTROLS",
        "POWER DISTRIBUTION & GEAR",
        "ENERGY SOLUTIONS",
        "TRAINING & SKILLS MANAGEMENT",
        "CONSTRUCTION SERVICES",
        "GOVERNMENT & EDUCATION",
        "MATERIAL MANAGEMENT & LOGISTICS",
        "MANUFACTURING & PLANT SERVICES",
        "SECURITY, FIRE & SURVEILLANCE",
        "NETWORKING & CYBERSECURITY",
    ];

    const branchNames: string[] = [];

    const [isLoading, setLoading] = useState(true);
    useEffect(() => {
        if (backupCompanyData.length === 0 && !selectedRole && !selectedCategory && !selectedBranchName) {
            loadCompanyDirectory({
                onSuccess: responseData => assignCompanyDataToVariable(responseData),
            });
        }
        if (copyData && copyData.length > 0) {
            setCompanyData(companyData.splice(0, companyData.length));
            copyData.forEach((val: any) => companyData.push({ ...val }));
            setCompanyData(companyData);
        }

        const parsedQuery = { role: "", category: "", branchName: "" };
        parsedQuery.role = getQueryStringValue("role");
        parsedQuery.category = getQueryStringValue("category").replace("And", "&");
        parsedQuery.branchName = getQueryStringValue("branchName").replace("And", "&");
        if (parsedQuery.role) {
            setSelectedRole(parsedQuery.role);
            setIsPageReloadRequired(true);
        }
        if (parsedQuery.category) {
            setSelectedCategory(parsedQuery.category);
            setIsPageReloadRequired(true);
        }
        if (parsedQuery.branchName) {
            setSelectedBranchName(parsedQuery.branchName);
            setIsPageReloadRequired(true);
        }

        if (parsedQuery.role || parsedQuery.category || parsedQuery.branchName) {
            changeFiltersInUrl({}, window.location.origin + window.location.pathname);
        }
        if (
            backupCompanyData.length > 0 &&
            selectedRole &&
            selectedCategory &&
            selectedBranchName &&
            isPageReloadRequired
        ) {
            fiterCompanyRecords(selectedRole, selectedCategory, selectedBranchName);
        } else if (backupCompanyData.length > 0 && isPageReloadRequired) {
            fiterCompanyRecords(selectedRole, selectedCategory?.replace("And", "&"), selectedBranchName);
        }
    }, [backupCompanyData, selectedRole, selectedCategory, selectedBranchName]);

    const assignCompanyDataToVariable = (companyDataObj: any) => {
        if (companyDataObj && companyData.length === 0) {
            setCompanyData(companyDataObj);
            setBackupCompanyData(companyDataObj);
            setBranchNameDropdownValues(companyDataObj);
            const companyData = cloneDeep(companyDataObj);
            setCopyData(companyData);
            setLoading(false);
            setIsPageReloadRequired(true);
        }
    };

    const setBranchNameDropdownValues = (companyDataObj: any) => {
        const flags = [];
        const l = companyDataObj.length;
        let i;
        for (i = 0; i < l; i++) {
            if (flags[companyDataObj[i]["BRANCH NAME"]]) {
                continue;
            }
            flags[companyDataObj[i]["BRANCH NAME"]] = true;
            if (companyDataObj[i]["BRANCH NAME"]) {
                branchNames.push(companyDataObj[i]["BRANCH NAME"]);
            }
        }
        if (branchNames) {
            setBranchNameData(branchNames);
        }
    };

    const RoleChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const role = event.target.value;
        if (role) {
            setSelectedRole(role);
            setIsPageReloadRequired(true);
        }
    };

    const CategoryChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const category = event.target.value;
        if (category) {
            setSelectedCategory(category);
            setIsPageReloadRequired(true);
        }
    };

    const branchNameChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const branchName = event.target.value;
        if (branchName) {
            setSelectedBranchName(branchName);
            setIsPageReloadRequired(true);
        }
    };

    const fiterCompanyRecords = (role?: string, category?: string, branchName?: string): void => {
        let filteredRecords: any;
        if (role) {
            if (filteredRecords) {
                filteredRecords = filteredRecords.filter((x: any) => x[role] === "X" || x[role] === "x");
            } else {
                filteredRecords = companyData.filter((x: any) => x[role] === "X" || x[role] === "x");
            }
            updateCompanyData(filteredRecords, role, category, branchName, setCompanyData, companyData);
        }
        if (category) {
            if (filteredRecords) {
                filteredRecords = filteredRecords.filter((x: any) => x[category] === "X" || x[category] === "x");
            } else {
                filteredRecords = companyData.filter((x: any) => x[category] === "X" || x[category] === "x");
            }
            updateCompanyData(filteredRecords, role, category, branchName, setCompanyData, companyData);
        }
        if (branchName) {
            if (filteredRecords) {
                filteredRecords = filteredRecords.filter(
                    (x: any) =>
                        x["BRANCH NAME"] === branchName ||
                        x["BRANCH NAME"] === branchName.toLowerCase() ||
                        x["BRANCH NAME"] === branchName.toUpperCase(),
                );
            } else {
                filteredRecords = companyData.filter(
                    (x: any) =>
                        x["BRANCH NAME"] === branchName ||
                        x["BRANCH NAME"] === branchName.toLowerCase() ||
                        x["BRANCH NAME"] === branchName.toUpperCase(),
                );
            }
            updateCompanyData(filteredRecords, role, category, branchName, setCompanyData, companyData);
        }
        setIsPageReloadRequired(false);
    };

    const updateCompanyData = (
        filteredRecords: any,
        role: any,
        category: any,
        branchName: any,
        setCompanyData: React.Dispatch<any>,
        companyData: any,
    ) => {
        if ((filteredRecords && filteredRecords.length > 0) || (filteredRecords && (role || category || branchName))) {
            setCompanyData(companyData.splice(0, companyData.length));
            filteredRecords.forEach((val: any) => companyData.push({ ...val }));
            setCompanyData(companyData);
        }
    };

    const ClearFiterHandler = () => {
        setCompanyData(backupCompanyData);
        setSelectedCategory("");
        setSelectedRole("");
        setSelectedBranchName("");
        if (copyData && copyData.length > 0) {
            setCompanyData(companyData.splice(0, companyData.length));
            copyData.forEach((val: any) => companyData.push({ ...val }));
            setCompanyData(companyData);
        }
        changeFiltersInUrl({}, window.location.origin + window.location.pathname);
    };

    const changeFiltersInUrl = (data: any, url: string) => {
        window.history.replaceState({}, document.title, url);
    };
    const styles = CustomCompanyDirectoryStyles;

    return (
        <>
            {isLoading ? (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            ) : (
                <>
                    <GridItem {...styles.companyDirectoryGridItem}>
                        <StyledWrapper {...styles.filterWrapper}>
                            <StyledWrapper {...styles.filterHeading}>
                                <Typography variant="h2">Filter By Role</Typography>
                            </StyledWrapper>
                            <StyledWrapper>
                                <Select onChange={e => RoleChangeHandler(e)} value={selectedRole}>
                                    <option value="" disabled>
                                        {translate("Select Role")}
                                    </option>
                                    {roles.map((role, i) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <option key={i} value={role}>
                                            {role}
                                        </option>
                                    ))}
                                </Select>
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.filterWrapper}>
                            <StyledWrapper {...styles.filterHeading}>
                                <Typography variant="h2">Filter By Category</Typography>
                            </StyledWrapper>
                            <StyledWrapper>
                                <Select onChange={e => CategoryChangeHandler(e)} value={selectedCategory}>
                                    <option value="" disabled>
                                        {translate("Select Category")}
                                    </option>
                                    {categories.map((category, i) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <option key={i} value={category}>
                                            {category}
                                        </option>
                                    ))}
                                </Select>
                            </StyledWrapper>
                        </StyledWrapper>

                        <StyledWrapper {...styles.filterWrapper}>
                            <StyledWrapper {...styles.filterHeading}>
                                <Typography variant="h2">Filter By Branch</Typography>
                            </StyledWrapper>
                            <StyledWrapper>
                                <Select onChange={e => branchNameChangeHandler(e)} value={selectedBranchName}>
                                    <option value="" disabled>
                                        {translate("Select Branch Name")}
                                    </option>
                                    {branchNamesData
                                        .slice() // Create a shallow copy to avoid mutating the original array
                                        .sort((a, b) => a.localeCompare(b)) // Sort the array alphabetically
                                        .map((branchName, i) => (
                                            // eslint-disable-next-line react/no-array-index-key
                                            <option key={i} value={branchName}>
                                                {branchName}
                                            </option>
                                        ))}
                                </Select>
                            </StyledWrapper>
                        </StyledWrapper>

                        <StyledWrapper {...styles.companyDirectoryFilter}>
                            <Button onClick={ClearFiterHandler} data-test-selector="clear_filter_blogs">
                                {translate("CLEAR FILTERS")}
                            </Button>
                        </StyledWrapper>
                    </GridItem>
                    <StyledWrapper {...styles.container} data-test-selector="companyDirectoryTable">
                        <DataTable {...styles.dataTable}>
                            <DataTableHead>
                                <DataTableHeader>{translate("")}</DataTableHeader>
                                <DataTableHeader>{translate("NAME")}</DataTableHeader>
                                <DataTableHeader>{translate("TITLE")}</DataTableHeader>
                                <DataTableHeader>{translate("EMAIL")}</DataTableHeader>
                                <DataTableHeader>{translate("WORK")}</DataTableHeader>
                                <DataTableHeader>{translate("BRANCH NAME")}</DataTableHeader>
                                <DataTableHeader>{translate("TERRITORY")}</DataTableHeader>
                            </DataTableHead>
                            <DataTableBody data-test-selector="companyDirectory_tableBody">
                                {companyData?.map((companyRow: any) => {
                                    return (
                                        <DataTableRow key={Math.random().toString()}>
                                            <DataTableCell>
                                                {companyRow["LinkedIn"] && (
                                                    <Link href={companyRow["LinkedIn"]} target="_blank">
                                                        {" "}
                                                        <img
                                                            alt=""
                                                            src="/userfiles/image/footer/icon-awesome-linkedin-in.png"
                                                            {...styles.imgStyle}
                                                        ></img>
                                                    </Link>
                                                )}
                                            </DataTableCell>
                                            <DataTableCell>
                                                {companyRow["FIRST NAME"]} {companyRow["LAST NAME"]}{" "}
                                            </DataTableCell>
                                            <DataTableCell>{companyRow["TITLE"]}</DataTableCell>
                                            <DataTableCell>
                                                <Link href={`mailto:${companyRow["E-MAIL"]}`} target="_blank">
                                                    <Icon {...styles.iconStyle} size={14} src={Mail} />
                                                </Link>
                                            </DataTableCell>
                                            <DataTableCell>
                                                <Link href={`tel:+ ${companyRow["WORK"]}`} target="_blank">
                                                    {companyRow["WORK"]}
                                                </Link>
                                            </DataTableCell>
                                            <DataTableCell>{companyRow["BRANCH NAME"]}</DataTableCell>
                                            <DataTableCell>{companyRow["TERR"]}</DataTableCell>
                                        </DataTableRow>
                                    );
                                })}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                </>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CompanyDirectoryGrid),
    definition: {
        group: "Common",
        displayName: "Company Directory Grid",
    },
};

export default widgetModule;
